import React from 'react';
import { Link } from 'react-router-dom';
import { formatDateShort, getImageUrl } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

export const HeadlineStory = ({ article, loading = false }) => {
  if (loading) {
    return (
      <section className="relative" data-testid="headline-story-loading">
        <Skeleton className="w-full h-[40vh] md:h-[55vh]" />
        <div className="container-custom py-6">
          <Skeleton className="h-10 w-3/4 mb-3" />
          <Skeleton className="h-5 w-1/2 mb-4" />
          <Skeleton className="h-4 w-40" />
        </div>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="relative h-[40vh] bg-muted flex items-center justify-center" data-testid="headline-story-empty">
        <div className="text-center">
          <p className="text-muted-foreground text-lg">No headline story set</p>
          <p className="text-muted-foreground text-sm mt-2">Set a headline story from the admin dashboard</p>
        </div>
      </section>
    );
  }

  const imageUrl = article.hero_image 
    ? getImageUrl(article.hero_image)
    : 'https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=1920&h=1080&fit=crop';

  return (
    <section data-testid="headline-story">
      {/* Section Label */}
      <div className="container-custom py-4">
        <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
          Top Story
        </span>
      </div>

      <Link 
        to={`/article/${article.slug}`}
        className="block group"
        data-testid="headline-story-link"
      >
        {/* Hero Image — full width, unobstructed */}
        <div className="relative w-full h-[40vh] md:h-[55vh] overflow-hidden">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=1920&h=1080&fit=crop'; }}
          />
        </div>

        {/* Text below the image */}
        <div className="bg-foreground text-background">
          <div className="container-custom py-6 md:py-8">
            <div className="max-w-3xl">
              {/* Category */}
              {article.category && (
                <span className="inline-block px-3 py-1 bg-primary text-primary-foreground text-xs font-mono uppercase tracking-wider mb-3">
                  {article.category.name}
                </span>
              )}

              {/* Title */}
              <h1 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3">
                {article.title}
              </h1>

              {/* Subheadline */}
              {article.subheadline && (
                <p className="text-background/70 text-base md:text-lg line-clamp-2 mb-3">
                  {article.subheadline}
                </p>
              )}

              {/* Author + Date + CTA */}
              <div className="flex items-center gap-4">
                <span className="text-background/50 text-sm font-mono uppercase tracking-wider">
                  {article.author}
                </span>
                <span className="text-background/30">·</span>
                <span className="text-background/50 text-sm font-mono uppercase tracking-wider">
                  {formatDateShort(article.publish_date || article.created_at)}
                </span>
                <span className="ml-auto text-sm font-medium text-primary group-hover:underline hidden md:inline">
                  Read More →
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default HeadlineStory;
